import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router';

export default function ScrollToTop() {
  const { pathname, hash, key } = useLocation();
  const navigationType = useNavigationType();
  const scrollPositions = useRef<Map<string, number>>(new Map());
  const lastPathname = useRef(pathname);

  // Save scroll position before leaving
  useEffect(() => {
    const saveScrollPosition = () => {
      scrollPositions.current.set(key, window.scrollY);
    };

    window.addEventListener('beforeunload', saveScrollPosition);
    return () => {
      saveScrollPosition();
      window.removeEventListener('beforeunload', saveScrollPosition);
    };
  }, [key]);

  // Handle hash scrolling (key changes on each navigation, even to the same URL)
  useEffect(() => {
    if (hash) {
      // Small delay to ensure the element is rendered
      setTimeout(() => {
        const element = document.getElementById(hash.slice(1));
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 0);
    }
  }, [hash, pathname, key]);

  // Restore or reset scroll position (only on pathname changes, not search param changes)
  useEffect(() => {
    // Skip if only search params changed (e.g., modal opened)
    if (pathname === lastPathname.current) {
      return;
    }
    lastPathname.current = pathname;

    // Skip if there's a hash - let the hash effect handle scrolling
    if (hash) {
      return;
    }

    if (navigationType === 'POP') {
      // Back/forward navigation - restore saved position
      const savedPosition = scrollPositions.current.get(key);
      if (savedPosition !== undefined) {
        window.scrollTo(0, savedPosition);
      }
    } else {
      // PUSH or REPLACE - scroll to top
      window.scrollTo(0, 0);
    }
  }, [pathname, hash, key, navigationType]);

  return null;
}
